import { percentualParaTaxa, planoParametrosSchema, type PlanoParametrosDto, type PlanoParametrosResponse, type RegraFreteNet } from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo, fracaoParaPercentualTexto } from '@/lib/format';

/** Formulario de parametros: so texto digitado. Percentuais em "18" ou "1,65" (convertidos pelo contrato); fator e saldo em decimal. */
export interface ParametrosForm {
  regraFreteNet: RegraFreteNet;
  fatorFreteNet: string;
  freteIcmsPct: string;
  empresaNaoCumulativa: boolean;
  pesoPrevisao: string;
  icmsPadrao: string;
  pisPadrao: string;
  cofinsPadrao: string;
  comissaoPadrao: string;
  pendenteNaNecessidade: boolean;
  saldoInicialFluxo: string;
}

/** Valores da API no texto dos campos (fracao "0.18000000" vira "18"; inverso exato da conversao de volta). */
export function parametrosFormDe(p: PlanoParametrosResponse): ParametrosForm {
  return {
    regraFreteNet: p.regraFreteNet,
    fatorFreteNet: decimalParaTextoCampo(p.fatorFreteNet ?? ''),
    freteIcmsPct: fracaoParaPercentualTexto(p.freteIcmsPct),
    empresaNaoCumulativa: p.empresaNaoCumulativa,
    pesoPrevisao: fracaoParaPercentualTexto(p.pesoPrevisao),
    icmsPadrao: fracaoParaPercentualTexto(p.icmsPadrao),
    pisPadrao: fracaoParaPercentualTexto(p.pisPadrao),
    cofinsPadrao: fracaoParaPercentualTexto(p.cofinsPadrao),
    comissaoPadrao: fracaoParaPercentualTexto(p.comissaoPadrao),
    pendenteNaNecessidade: p.pendenteNaNecessidade,
    saldoInicialFluxo: decimalParaTextoCampo(p.saldoInicialFluxo),
  };
}

export type MontagemParametros = { ok: true; dto: PlanoParametrosDto } | { ok: false; mensagem: string };

const PERCENTUAIS: ReadonlyArray<readonly [keyof ParametrosForm, string]> = [
  ['freteIcmsPct', 'ICMS do frete'],
  ['pesoPrevisao', 'Peso da Previsão'],
  ['icmsPadrao', 'ICMS padrão'],
  ['pisPadrao', 'PIS padrão'],
  ['cofinsPadrao', 'COFINS padrão'],
  ['comissaoPadrao', 'Comissão padrão'],
];

/** Texto do formulario para o corpo da API. A validacao final e o schema compartilhado do plano. */
export function montarParametros(f: ParametrosForm): MontagemParametros {
  for (const [campo, rotulo] of PERCENTUAIS) {
    if (percentualParaTaxa(String(f[campo])) === null) return { ok: false, mensagem: `${rotulo}: informe um percentual (ex.: 18 ou 1,65)` };
  }
  let fatorFreteNet: string | null = null;
  if (f.regraFreteNet === 'FATOR_FIXO') {
    const fator = decimalDigitado(f.fatorFreteNet);
    if (fator === null) return { ok: false, mensagem: 'Fator do frete net: informe um número entre 0 e 1 (ex.: 0,9)' };
    fatorFreteNet = fator;
  }
  const saldo = decimalDigitado(f.saldoInicialFluxo);
  if (saldo === null) return { ok: false, mensagem: 'Saldo inicial do fluxo: informe um valor (ex.: 1.500,00)' };

  const taxa = (texto: string) => percentualParaTaxa(texto) ?? '';
  const dto: PlanoParametrosDto = {
    regraFreteNet: f.regraFreteNet,
    fatorFreteNet,
    freteIcmsPct: taxa(f.freteIcmsPct),
    empresaNaoCumulativa: f.empresaNaoCumulativa,
    pesoPrevisao: taxa(f.pesoPrevisao),
    icmsPadrao: taxa(f.icmsPadrao),
    pisPadrao: taxa(f.pisPadrao),
    cofinsPadrao: taxa(f.cofinsPadrao),
    comissaoPadrao: taxa(f.comissaoPadrao),
    pendenteNaNecessidade: f.pendenteNaNecessidade,
    saldoInicialFluxo: saldo,
  };
  const valido = planoParametrosSchema.safeParse(dto);
  if (!valido.success) return { ok: false, mensagem: valido.error.issues[0]?.message ?? 'Parâmetros inválidos' };
  return { ok: true, dto: valido.data };
}
